import { bufferToHex } from '@exodus/ethereumjs/util'
import SolidityContract from '@exodus/solidity-contract'
import EventEmitter from 'events'
import lodash from 'lodash'

import { fromHexToString } from '../number-utils.js'

const { isEmpty } = lodash

export default class ApiCoinNodesServer extends EventEmitter {
  constructor({ uri }) {
    super()
    this.uri = uri
    this.defaultUri = uri
    this.id = 0
  }

  setURI(uri) {
    this.uri = uri
  }

  async request(body) {
    const options = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }

    const response = await fetch(this.uri, options)
    return response.json()
  }

  async sendBatchRequest(batch) {
    if (isEmpty(batch)) return batch
    const responses = await this.request(batch)
    const isValid = responses.every((response) => {
      return !isNaN(response?.id) && response?.result
    })
    if (responses.length !== batch.length || !isValid) {
      throw new Error('Bad rpc batch response')
    }

    const keyed = responses.reduce((acc, response) => {
      return { ...acc, [`${response.id}`]: response.result }
    }, Object.create(null))
    return batch.map((request) => keyed[`${request.id}`])
  }

  async sendRequest(request) {
    const response = await this.request(request)

    const result = response?.result
    const error = response?.error
    if (error || result === undefined) {
      const message = error?.message || error?.code || 'no result'
      throw new Error(`Bad rpc response: ${message}`)
    }

    return result
  }

  async isContract(address) {
    const code = await this.getCode(address)
    return code.length > 2
  }

  buildRequest({ method, params = [] }) {
    return { jsonrpc: '2.0', id: this.id++, method, params }
  }

  balanceOfRequest(address, tokenAddress, tag = 'latest') {
    const contract = SolidityContract.simpleErc20(tokenAddress)
    const callData = contract.balanceOf.build(address)
    const data = {
      data: bufferToHex(callData),
      to: tokenAddress,
    }
    return this.ethCallRequest(data, tag)
  }

  getBalanceRequest(address, tag = 'latest') {
    return this.buildRequest({ method: 'eth_getBalance', params: [address, tag] })
  }

  gasPriceRequest() {
    return this.buildRequest({ method: 'eth_gasPrice' })
  }

  estimateGasRequest(data) {
    return this.buildRequest({ method: 'eth_estimateGas', params: [data] })
  }

  sendRawTransactionRequest(data) {
    const _data = data instanceof Uint8Array ? Buffer.from(data).toString('hex') : data
    const hex = _data.startsWith('0x') ? _data : '0x' + _data
    return this.buildRequest({ method: 'eth_sendRawTransaction', params: [hex] })
  }

  getCodeRequest(address, tag = 'latest') {
    return this.buildRequest({ method: 'eth_getCode', params: [address, tag] })
  }

  getStorageAtRequest(address, position, tag = 'latest') {
    return this.buildRequest({ method: 'eth_getStorageAt', params: [address, position, tag] })
  }

  getTransactionCountRequest(address, tag = 'latest') {
    return this.buildRequest({ method: 'eth_getTransactionCount', params: [address, tag] })
  }

  getTransactionByHashRequest(hash) {
    return this.buildRequest({ method: 'eth_getTransactionByHash', params: [hash] })
  }

  getTransactionReceiptRequest(txhash) {
    return this.buildRequest({ method: 'eth_getTransactionReceipt', params: [txhash] })
  }

  ethCallRequest(data, tag = 'latest') {
    return this.buildRequest({ method: 'eth_call', params: [data, tag] })
  }

  blockNumberRequest() {
    return this.buildRequest({ method: 'eth_blockNumber' })
  }

  getBlockByNumberRequest(numberHex, isFullTxs = false) {
    return this.buildRequest({ method: 'eth_getBlockByNumber', params: [numberHex, isFullTxs] })
  }

  simulateRawTransactionRequest(rawTx, applyPending = true) {
    const replaced = rawTx.replace('0x', '')
    return this.buildRequest({
      method: 'debug_simulateRawTransaction',
      params: [replaced, applyPending],
    })
  }

  async balanceOf(address, tokenAddress, tag = 'latest') {
    const request = this.balanceOfRequest(address, tokenAddress, tag)
    const result = await this.sendRequest(request)
    const balance = fromHexToString(result)
    return {
      confirmed: {
        [tokenAddress]: balance,
      },
    }
  }

  async getBalance(...params) {
    const request = this.getBalanceRequest(...params)
    return this.sendRequest(request)
  }

  async getBalanceProxied(...params) {
    return this.getBalance(...params)
  }

  async gasPrice(...params) {
    const request = this.gasPriceRequest(...params)
    return this.sendRequest(request)
  }

  // for fee monitor
  getGasPrice = this.gasPrice

  async getLatestBlock() {
    const request = this.buildRequest({
      method: 'eth_getBlockByNumber',
      params: ['latest', false],
    })
    return this.sendRequest(request)
  }

  async getBaseFeePerGas() {
    const response = await this.getLatestBlock()
    if (response.baseFeePerGas) {
      return fromHexToString(response.baseFeePerGas)
    }
  }

  async estimateGas(...params) {
    const request = this.estimateGasRequest(...params)
    return this.sendRequest(request)
  }

  async sendRawTransaction(...params) {
    const request = this.sendRawTransactionRequest(...params)
    return this.sendRequest(request)
  }

  async getCode(...params) {
    const request = this.getCodeRequest(...params)
    return this.sendRequest(request)
  }

  async getStorageAt(...params) {
    const request = this.getStorageAtRequest(...params)
    return this.sendRequest(request)
  }

  async getTransactionCount(...params) {
    const request = this.getTransactionCountRequest(...params)
    return this.sendRequest(request)
  }

  async getTransactionByHash(...params) {
    const request = this.getTransactionByHashRequest(...params)
    return this.sendRequest(request)
  }

  async getTransactionReceipt(...params) {
    const request = this.getTransactionReceiptRequest(...params)
    return this.sendRequest(request)
  }

  async ethCall(...params) {
    const request = this.ethCallRequest(...params)
    return this.sendRequest(request)
  }

  async blockNumber(...params) {
    const request = this.blockNumberRequest(...params)
    return this.sendRequest(request)
  }

  async getBlockByNumber(...params) {
    const request = this.getBlockByNumberRequest(...params)
    return this.sendRequest(request)
  }

  async simulateRawTransaction(...params) {
    const request = this.simulateRawTransactionRequest(...params)
    return this.sendRequest(request)
  }

  stop() {
    // no web socket to stop!
  }
}
